import { ThemeProvider, createTheme } from "@mui/material/styles";
import { SnackbarProvider } from "notistack";
import { useState } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { Route, BrowserRouter as Router, Routes, Navigate } from "react-router-dom";
import { SupabaseProvider } from "./context/SupabaseContext";
import { AuthContextProvider } from "./context/AuthContext";
import { LoginView } from "./views/home/LoginView";
import { PrivacyPolicyView } from "./views/home/PrivacyPolicyView";
import { SupportView } from "./views/home/SupportView";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminLayout from "./components/AdminLayout";
import ErrorBoundary from "./components/ErrorBoundary";

const queryClient = new QueryClient();

const theme = createTheme({
  palette: {
    primary: {
      main: "#8a1125",
      light: "rgba(138, 17, 37, 0.08)",
      dark: "#6b0d1d",
    },
    secondary: {
      main: "#10324a",
      light: "#1a4a6e",
      dark: "#0a2235",
    },
    bg: {
      main: "#f8f9fb",
    },
    error: {
      main: "#F42C04",
      light: "rgb(255 172 172)",
      dark: "#b80000",
    },
    warning: {
      main: "#FFF275",
      light: "#fff8b0",
    },
    info: {
      main: "#0288d1",
    },
    success: {
      main: "#008000",
      light: "#cbffcb",
    },
  },
  typography: {
    fontFamily: '"DM Sans", system-ui, -apple-system, sans-serif',
    h1: { fontFamily: '"Outfit", "DM Sans", sans-serif', fontWeight: 700 },
    h2: { fontFamily: '"Outfit", "DM Sans", sans-serif', fontWeight: 700 },
    h3: { fontFamily: '"Outfit", "DM Sans", sans-serif', fontWeight: 600 },
    h4: { fontFamily: '"Outfit", "DM Sans", sans-serif', fontWeight: 600 },
    h5: { fontFamily: '"Outfit", "DM Sans", sans-serif', fontWeight: 600 },
    h6: { fontFamily: '"Outfit", "DM Sans", sans-serif', fontWeight: 600 },
    button: { textTransform: "none", fontWeight: 600 },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "10px",
          textTransform: "none",
          fontWeight: 600,
          padding: "8px 20px",
        },
        contained: {
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          boxShadow:
            "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
          border: "1px solid #e2e5ea",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          boxShadow:
            "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
          border: "1px solid #e2e5ea",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          color: "#5f6877",
          fontSize: "13px",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          borderBottom: "2px solid #e2e5ea",
        },
        body: {
          fontSize: "14px",
          color: "#1a1d23",
          borderBottom: "1px solid #eef0f3",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
            "&.Mui-focused fieldset": {
              borderColor: "#10324a",
            },
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: "#10324a",
          },
        },
      },
    },
  },
});

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <QueryClientProvider client={queryClient}>
            <AuthContextProvider>
              <SupabaseProvider>
                <Router>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/login" element={<LoginView />} />
                    <Route path="/privacy" element={<PrivacyPolicyView />} />
                    <Route path="/support" element={<SupportView />} />

                    {/* Redirect root to admin overview */}
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <Navigate to="/admin/overview" replace />
                        </ProtectedRoute>
                      }
                    />

                    {/* Protected admin routes */}
                    <Route
                      path="/admin/*"
                      element={
                        <ProtectedRoute>
                          <AdminLayout
                            sidebarOpen={sidebarOpen}
                            setSidebarOpen={setSidebarOpen}
                          />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </Router>
              </SupabaseProvider>
            </AuthContextProvider>
          </QueryClientProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
